/**
 * Date Range Picker Field Component
 *
 * A date range picker with quick preset buttons using MUI components.
 * Provides common date ranges (This Month, Last Month, YTD, etc.) for faster filtering.
 */

import React, { useState, useRef } from 'react'
import {
  Box,
  TextField,
  Button,
  Popover,
  Typography,
  Divider,
  Stack,
  InputAdornment,
  IconButton,
  type SxProps,
  type Theme,
} from '@mui/material'
import { CalendarMonth, Clear } from '@mui/icons-material'
import {
  startOfMonth,
  endOfMonth,
  startOfQuarter,
  endOfQuarter,
  subMonths,
  subQuarters,
  format,
  parse,
  isValid,
} from 'date-fns'
import { poolwerxColors } from '../../../../theme/colors'

interface DateRangePickerFieldProps {
  startDate: string
  endDate: string
  onChange: (startDate: string, endDate: string) => void
  disabled?: boolean
  presets?: DatePreset[]
  hideLabel?: boolean
  sx?: SxProps<Theme>
}

export interface DatePreset {
  label: string
  getRange: () => { startDate: Date; endDate: Date }
}

const getDatePresets = (): DatePreset[] => {
  const today = new Date()

  return [
    {
      label: 'This Month',
      getRange: () => ({
        startDate: startOfMonth(today),
        endDate: today,
      }),
    },
    {
      label: 'Last Month',
      getRange: () => ({
        startDate: startOfMonth(subMonths(today, 1)),
        endDate: endOfMonth(subMonths(today, 1)),
      }),
    },
    {
      label: 'This Quarter',
      getRange: () => ({
        startDate: startOfQuarter(today),
        endDate: today,
      }),
    },
    {
      label: 'Last Quarter',
      getRange: () => ({
        startDate: startOfQuarter(subQuarters(today, 1)),
        endDate: endOfQuarter(subQuarters(today, 1)),
      }),
    },
    {
      label: 'YTD',
      getRange: () => ({
        startDate: new Date(today.getFullYear(), 0, 1),
        endDate: today,
      }),
    },
    {
      label: 'Last 12 Months',
      getRange: () => ({
        startDate: subMonths(today, 12),
        endDate: today,
      }),
    },
    {
      label: 'All',
      getRange: () => ({
        startDate: new Date(2000, 0, 1),
        endDate: today,
      }),
    },
  ]
}

const formatDateForInput = (date: Date): string => {
  return format(date, 'yyyy-MM-dd')
}

const formatDateForDisplay = (dateStr: string): string => {
  if (!dateStr) return ''
  try {
    const date = parse(dateStr, 'yyyy-MM-dd', new Date())
    if (!isValid(date)) return dateStr
    return format(date, 'MMM d, yyyy')
  } catch {
    return dateStr
  }
}

const DateRangePickerField: React.FC<DateRangePickerFieldProps> = ({
  startDate,
  endDate,
  onChange,
  disabled = false,
  presets: customPresets,
  hideLabel = false,
  sx: outerSx,
}) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const [localStartDate, setLocalStartDate] = useState(startDate)
  const [localEndDate, setLocalEndDate] = useState(endDate)
  const inputRef = useRef<HTMLDivElement>(null)

  const open = Boolean(anchorEl)

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    if (disabled) return
    setLocalStartDate(startDate)
    setLocalEndDate(endDate)
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleApply = () => {
    onChange(localStartDate, localEndDate)
    handleClose()
  }

  const handlePresetClick = (preset: DatePreset) => {
    const { startDate: start, endDate: end } = preset.getRange()
    const startStr = formatDateForInput(start)
    const endStr = formatDateForInput(end)
    setLocalStartDate(startStr)
    setLocalEndDate(endStr)
    onChange(startStr, endStr)
    handleClose()
  }

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation()
    onChange('', '')
  }

  const displayValue = startDate && endDate
    ? `${formatDateForDisplay(startDate)} - ${formatDateForDisplay(endDate)}`
    : startDate
    ? `From ${formatDateForDisplay(startDate)}`
    : endDate
    ? `Until ${formatDateForDisplay(endDate)}`
    : ''

  const resolvedPresets = customPresets ?? getDatePresets()

  return (
    <Box sx={outerSx}>
      {!hideLabel && (
        <Typography
          component="label"
          sx={{
            fontWeight: 600,
            color: 'text.secondary',
            fontSize: '0.75rem',
            mb: 0.5,
            display: 'block',
          }}
        >
          Date Range
        </Typography>
      )}
      <TextField
        ref={inputRef}
        fullWidth
        size="small"
        placeholder="Select date range"
        value={displayValue}
        onClick={handleOpen}
        disabled={disabled}
        InputProps={{
          readOnly: true,
          startAdornment: (
            <InputAdornment position="start">
              <CalendarMonth sx={{ color: 'action.active', fontSize: 20 }} />
            </InputAdornment>
          ),
          endAdornment: displayValue && !disabled ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={handleClear}
                sx={{ p: 0.5 }}
              >
                <Clear sx={{ fontSize: 18 }} />
              </IconButton>
            </InputAdornment>
          ) : null,
          sx: {
            cursor: disabled ? 'default' : 'pointer',
            '& input': { cursor: disabled ? 'default' : 'pointer' },
          },
        }}
      />
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: { mt: 1, minWidth: 380 },
          },
        }}
      >
        <Box sx={{ display: 'flex' }}>
          {/* Presets sidebar */}
          <Box
            sx={{
              width: 140,
              borderRight: 1,
              borderColor: 'divider',
              py: 1,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 2,
                py: 1,
                display: 'block',
                color: 'text.secondary',
                fontWeight: 600,
              }}
            >
              Quick Select
            </Typography>
            {resolvedPresets.map((preset) => (
              <Button
                key={preset.label}
                fullWidth
                size="small"
                onClick={() => handlePresetClick(preset)}
                sx={{
                  justifyContent: 'flex-start',
                  px: 2,
                  py: 0.75,
                  textTransform: 'none',
                  color: 'text.primary',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: poolwerxColors.tealLight,
                  },
                }}
              >
                {preset.label}
              </Button>
            ))}
          </Box>

          {/* Date inputs */}
          <Box sx={{ p: 2, flex: 1 }}>
            <Typography
              variant="subtitle2"
              sx={{ mb: 2, color: 'text.secondary' }}
            >
              Custom Range
            </Typography>
            <Stack spacing={2}>
              <Box>
                <Typography
                  component="label"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  Start Date
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={localStartDate}
                  onChange={(e) => setLocalStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
              <Box>
                <Typography
                  component="label"
                  sx={{
                    fontWeight: 600,
                    color: 'text.secondary',
                    fontSize: '0.75rem',
                    mb: 0.5,
                    display: 'block',
                  }}
                >
                  End Date
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  type="date"
                  value={localEndDate}
                  onChange={(e) => setLocalEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                />
              </Box>
            </Stack>
            <Divider sx={{ my: 2 }} />
            <Stack direction="row" spacing={1} justifyContent="flex-end">
              <Button
                size="small"
                onClick={handleClose}
                sx={{ textTransform: 'none' }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleApply}
                sx={{
                  textTransform: 'none',
                  backgroundColor: poolwerxColors.teal,
                  '&:hover': {
                    backgroundColor: poolwerxColors.tealHover,
                  },
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        </Box>
      </Popover>
    </Box>
  )
}

export default DateRangePickerField
